import {
  CreateSolutionInstanceOverride,
  GetEventOverrides,
  GetSolutionActions,
  UpdateSolutionInstanceOverrides,
} from '@/connection/integrations/integrations';
import { getWorkingProject } from '@/store/slices/project/selectors';
import {
  AvailableInstance,
  EIntegrationOverrideType,
  InsertMapOverridesBody,
  IntegrationActionDB,
  IntegrationsMappedOptions,
} from '@/types/integrations.types';
import Container from '@ui/container/container';
import Modal from '@ui/modal/modal';
import { useEffect, useState } from 'react';
import EventIntegrationOverridesForm from './event-integration-overrides-form';
import { useAppSelector } from '@/store/hooks';

import './event-integration-overrides-modal.css';

import Button from '@ui/buttons/button/button';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import Text from '@ui/text/text/text';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

interface EventIntegrationOverridesModalProps {
  instance: AvailableInstance;
  solutionTitle?: string;
  onClose: () => void;
}

function EventIntegrationOverridesModal({ instance, onClose, solutionTitle }: EventIntegrationOverridesModalProps) {
  const workingProject = useAppSelector(getWorkingProject);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [existingOverrides, setExistingOverrides] = useState<IntegrationsMappedOptions>();
  const [integrationActions, setIntegrationActions] = useState<IntegrationActionDB[]>([]);
  const [formIsValid, setFormIsValid] = useState(true);

  useEffect(() => {
    if (!isLoading || !instance.instance_uuid || !workingProject) return;

    const loadExistingOverrides = async () => {
      // Load existing overrides
      const [{ entity_mapping }, integrationActions] = await Promise.all([
        GetEventOverrides(instance.instance_uuid, workingProject.uuid, workingProject.channel),
        GetSolutionActions(instance.solution_uuid),
      ]);

      if (entity_mapping) {
        setExistingOverrides({
          ...entity_mapping,
          action: {
            action_endpoint: entity_mapping.action_endpoint,
            action_label: entity_mapping.action_label,
            action_description: entity_mapping.action_description,
          },
        });
      } else {
        const action = integrationActions[0] ?? {
          action_endpoint: 'custom',
          action_label: 'Custom',
          action_description: '',
        };

        setExistingOverrides({
          registration_mapping: {},
          query_mapping: {},
          outbound_activity_configuration: { enabled_actions: {} },
          static_mapping: {},
          opt_in_mapping: '',
          action,
          action_title_keyname: '',
          confirmation_url_mapping: null,
          convert_non_ascii_characters: instance?.convert_non_ascii_characters,
        });
      }

      setIntegrationActions(integrationActions);

      setIsLoading(false);
    };
    loadExistingOverrides();
  }, [
    existingOverrides,
    instance.channel,
    instance?.convert_non_ascii_characters,
    instance.instance_uuid,
    instance.solution_uuid,
    isLoading,
    workingProject,
  ]);

  const handleSave = async () => {
    if (!workingProject || !existingOverrides) return;
    setIsSaving(true);

    const payload: InsertMapOverridesBody = {
      ...existingOverrides,
      channel: instance.channel,
      entity_uuid: workingProject.uuid,
      type: EIntegrationOverrideType.event,
      instance_uuid: instance.instance_uuid,
    };

    try {
      if (existingOverrides.id) {
        await UpdateSolutionInstanceOverrides(payload);
      } else {
        await CreateSolutionInstanceOverride(payload);
      }
      onClose();
    } catch (error) {
      console.error('Error saving overrides', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = ({ overrides, valid }: { overrides?: IntegrationsMappedOptions; valid?: boolean }) => {
    if (overrides) {
      setExistingOverrides(overrides);
    }

    if (valid !== undefined) {
      setFormIsValid(valid);
    }
  };

  return (
    <Modal
      open={true}
      customSize={{
        width: '742px',
        height: '90%',
        maxWidth: '742px',
        maxHeight: '90%',
        minHeight: 'auto',
      }}
      portalClassName="event-integration-overrides-modal"
      showLoadingIndicator={isLoading}
      onRequestClose={onClose}
      contentWrapperClassName="padding-v-24 padding-h-40"
      RenderHeaderContent={() => <h3 className="H3 margin-b-20">Edit Integration</h3>}
      RenderFooterContent={() => (
        <Container className="flex fd-row jc-flex-end gap-16 margin-t-16">
          <button onClick={onClose}>Cancel</button>
          <Button onClick={handleSave} disabled={!formIsValid || isSaving} backgroundColor={getThemeColorCSSVariable()}>
            <Text color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}>
              Save
            </Text>
          </Button>
        </Container>
      )}
    >
      <EventIntegrationOverridesForm
        instance={instance}
        overrides={existingOverrides}
        actions={integrationActions}
        onChange={handleChange}
        solutionTitle={solutionTitle}
      />
    </Modal>
  );
}

export default EventIntegrationOverridesModal;
